import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Calculator, DollarSign, Percent } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/widgets/StatCard';
import { DataTable, Column } from '@/components/ui/DataTable';
import { useMarginAnalysis, useCostSimulation } from '@/hooks/useCosts';
import type { PlanMargin, SimulationResult } from '@/types/admin.types';

const formatCurrency = (amount: number, currency: string = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatPercent = (value: number) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

export const MarginAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30d');
  const { data: marginData, isLoading } = useMarginAnalysis(period);

  // Simulation state
  const [simPhotos, setSimPhotos] = useState('100');
  const [simAudios, setSimAudios] = useState('50');
  const [simTexts, setSimTexts] = useState('200');
  const [simPlanType, setSimPlanType] = useState('basic');
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);

  const simulateMutation = useCostSimulation();

  const handleSimulate = async () => {
    const result = await simulateMutation.mutateAsync({
      photos: parseInt(simPhotos),
      audios: parseInt(simAudios),
      texts: parseInt(simTexts),
      planType: simPlanType,
    });
    setSimResult(result);
  };

  const planColumns: Column<PlanMargin>[] = [
    {
      key: 'planName',
      label: 'Plano',
      render: (plan) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{plan.planName}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{plan.usersCount} usuarios</p>
        </div>
      ),
    },
    {
      key: 'revenue',
      label: 'Receita',
      align: 'right',
      render: (plan) => (
        <span className="font-medium">{formatCurrency(plan.revenue)}</span>
      ),
    },
    {
      key: 'costs',
      label: 'Custos',
      align: 'right',
      render: (plan) => (
        <span className="text-red-600 dark:text-red-400">{formatCurrency(plan.costs)}</span>
      ),
    },
    {
      key: 'profit',
      label: 'Lucro',
      align: 'right',
      render: (plan) => (
        <span className={plan.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
          {formatCurrency(plan.profit)}
        </span>
      ),
    },
    {
      key: 'marginPercent',
      label: 'Margem',
      align: 'center',
      render: (plan) => (
        <Badge variant={plan.marginPercent >= 50 ? 'success' : plan.marginPercent >= 20 ? 'warning' : 'danger'}>
          {plan.marginPercent.toFixed(1)}%
        </Badge>
      ),
    },
    {
      key: 'avgCostPerUser',
      label: 'Custo/Usuario',
      align: 'right',
      render: (plan) => (
        <span className="text-gray-600 dark:text-gray-400">{formatCurrency(plan.avgCostPerUser)}</span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/costs')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analise de Margem</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Margem de lucro por plano e simulador de custos
            </p>
          </div>
        </div>
        <Select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          options={[
            { value: '7d', label: 'Ultimos 7 dias' },
            { value: '30d', label: 'Ultimos 30 dias' },
            { value: '90d', label: 'Ultimos 90 dias' },
          ]}
          className="w-48"
        />
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Receita Total"
          value={formatCurrency(marginData?.totalRevenue || 0)}
          icon={<DollarSign className="w-6 h-6" />}
          description="Periodo selecionado"
        />
        <StatCard
          title="Custos Totais"
          value={formatCurrency(marginData?.totalCosts || 0)}
          icon={<TrendingDown className="w-6 h-6" />}
          description="APIs + Storage + WhatsApp"
        />
        <StatCard
          title="Lucro Bruto"
          value={formatCurrency(marginData?.totalProfit || 0)}
          icon={<TrendingUp className="w-6 h-6" />}
          trend={(marginData?.totalProfit || 0) >= 0 ? 'up' : 'down'}
          description="Receita - Custos"
        />
        <StatCard
          title="Margem Media"
          value={`${(marginData?.avgMarginPercent || 0).toFixed(1)}%`}
          icon={<Percent className="w-6 h-6" />}
          description="Todos os planos"
        />
      </div>

      {/* Margin by Plan */}
      <Card>
        <CardHeader>
          <CardTitle>Margem por Plano</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable
            data={marginData?.byPlan || []}
            columns={planColumns}
            keyExtractor={(plan) => plan.planId}
            emptyMessage="Nenhum dado disponivel"
          />
        </CardContent>
      </Card>

      {/* Cost Simulator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Simulador de Custos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Inputs */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 dark:text-white mb-4">Parametros de Uso</h3>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Fotos/mes"
                  type="number"
                  value={simPhotos}
                  onChange={(e) => setSimPhotos(e.target.value)}
                />
                <Input
                  label="Audios/mes"
                  type="number"
                  value={simAudios}
                  onChange={(e) => setSimAudios(e.target.value)}
                />
                <Input
                  label="Textos/mes"
                  type="number"
                  value={simTexts}
                  onChange={(e) => setSimTexts(e.target.value)}
                />
                <Select
                  label="Tipo de Plano"
                  value={simPlanType}
                  onChange={(e) => setSimPlanType(e.target.value)}
                  options={[
                    { value: 'basic', label: 'Basic' },
                    { value: 'pro', label: 'Pro' },
                    { value: 'enterprise', label: 'Enterprise' },
                  ]}
                />
              </div>
              <Button
                className="w-full"
                leftIcon={<Calculator className="w-4 h-4" />}
                onClick={handleSimulate}
                isLoading={simulateMutation.isPending}
              >
                Simular
              </Button>
            </div>

            {/* Results */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 dark:text-white mb-4">Resultado</h3>
              {simResult ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span className="text-gray-600 dark:text-gray-400">Custo Estimado</span>
                    <span className="font-semibold text-red-600 dark:text-red-400">
                      {formatCurrency(simResult.estimatedCost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span className="text-gray-600 dark:text-gray-400">Preco do Plano</span>
                    <span className="font-semibold">
                      {formatCurrency(simResult.planPrice)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span className="text-gray-600 dark:text-gray-400">Lucro Estimado</span>
                    <span className={`font-semibold ${simResult.estimatedProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatCurrency(simResult.estimatedProfit)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <span className="text-blue-700 dark:text-blue-300 font-medium">Margem</span>
                    <Badge variant={simResult.marginPercent >= 50 ? 'success' : simResult.marginPercent >= 20 ? 'warning' : 'danger'} size="lg">
                      {formatPercent(simResult.marginPercent)}
                    </Badge>
                  </div>
                  {simResult.marginPercent < 20 && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Atencao: Margem abaixo de 20%. Considere ajustar precos ou limites de uso.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                  <p>Configure os parametros e clique em Simular</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarginAnalysisPage;
